import React, { FC, useCallback, useEffect, useState, useRef } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";

// Utils
import { existsAndNotEmpty } from "stentor-utils";
import { useBoolState } from "utils/bool";
import { wrapUrqlMutation } from "utils/graphql";
// Library Components
import { Button, ButtonProps, Dialog, Onboarding, OnboardingStep, Place, Pricing } from "@xapp/components";
// Models
import { OnboardingProps } from "components/Onboarding/models";
// GraphQL
import { useGetUserProfileQuery, useGetWebsiteAnalysisQuery, useAnalyzeWebsiteMutation, useGetOrgQuery } from "generated/graphql";
// Components
import {
    AddressStep,
    LeadDestinationStep,
    IntentQuestionStep,
    WebsiteStep,
    SignUpStepContainer,
    ClaimAgentStep,
} from "../../components/V3";
// Containers
// These have a little more complex operations
import { TrainingStepContainer } from "./TrainingStepContainer";
import { FinalizeContainer } from "./FinalizeContainer";
import { ConfigureAndCreateStepContainer } from "./ConfigureAndCreateStepContainer";
import { ConfirmProfileContainer } from "./ConfirmProfileContainer";
import { CheckoutStepContainer } from "./CheckoutStepContainer";
import { AdvancedSearchStepContainer } from "./AdvancedSearchStepContainer";
import { WebsiteQuestionStepContainer } from "./WebsiteQuestionStepContainer";
import { FindAndConfirmProfileStepContainer } from "./FindAndConfirmProfileStepContainer";
import { AngiAutoResponderQuestionStepContainer } from "./AngiAutoresponderQuestionStepContainer";
import { FacebookPageQuestionStepContainer } from "./FacebookPageQuestionStepContainer";

// Utils
import { getStepAside, getStepHeader } from "./utils";
import { getDefaultProduct, getProduct, supportsIntegrations } from "utils/getProducts";
import { getCoupon } from "utils/getCoupons";
import { getLogoFromWebsite } from "components/Onboarding/utils/getLogoFromWebsite";
import { getTemplateFromId, TemplateId } from "components/Onboarding/utils/getTemplateFromPlace";
import { sendOnboardingEvent } from "components/Onboarding/utils/sendOnboardingEvent";

// Styles
import style from "./OnboardingV3Container.module.scss";
import { CustomizeStepContainer } from "./CustomizeStepContainer";
import { BusinessInfoStepContainer } from "./BusinessInfoStepContainer";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

function useAddQueryParams() {
    const history = useHistory();

    type QueryParamsType = { [key: string]: string };

    return useCallback(
        (newParams: QueryParamsType) => {
            const searchParams = new URLSearchParams(history.location.search);
            for (const key in newParams) {
                const value = newParams[key];
                // if value is null, remove the key
                if (value === null || value === undefined) {
                    searchParams.delete(key);
                } else if (value && value !== "null") {
                    searchParams.set(key, newParams[key]);
                }
            }
            history.replace({ ...history.location, search: searchParams.toString() });
        },
        [history]
    );
}

interface PathCondition {
    intent?: string[];
    needsWebsite?: boolean;
    needsPlace?: boolean;
    /**
     * Do they have either a website or a Google Business Profile?
     */
    canTrain?: boolean;
    /**
     * If the next step is only available for a specific partner
     */
    partner?: string[];
}

interface Path {
    /**
     * Next step, if not provided it will go to the next step in the array
     */
    nextStep?: string;
    /**
     * Used to go back to the previous step
     */
    prevStep?: string;
    /**
     * Current step in the onboarding, matches to the query string.
     */
    step: string;
    /**
     * Quickly disable the step
     */
    disabled?: boolean;
    /**
     * If true, it will clear the onboarding props when going to this step.
     */
    cleanProps?: boolean;

    /**
     * Conditions required to show the step.
     */
    condition?: PathCondition;
}

const PATH_A: Path[] = [
    {
        step: "find_profile",
        nextStep: "confirm_profile",
    },
    {
        step: "use_website",
        nextStep: "claim_agent",
        prevStep: "find_profile",
    },
    {
        step: "confirm_profile",
        nextStep: "sign_up",
        prevStep: "find_profile",
    },
    {
        step: "sign_up",
        nextStep: "create",
        prevStep: "claim_agent",
    },
    {
        step: "create",
        nextStep: "checkout",
    },
    {
        step: "checkout",
        nextStep: "finalize",
        prevStep: "checkout",
    },
];

// NEW PATH
const PATH_B: Path[] = [
    /*  {
        step: "intent",
        nextStep: "business_info",
    }, */
    {
        step: "business_info",
        nextStep: "find_confirm_profile",
        // prevStep: "intent",
        // cleanProps: true,
    },
    {
        step: "find_confirm_profile",
        prevStep: "business_info",
    },
    {
        step: "customize", // customize/you're eligible
        prevStep: "find_confirm_profile",
    },
    {
        step: "website",
        prevStep: "find_confirm_profile",
        condition: {
            needsWebsite: true,
        },
    },
    {
        step: "auto_responders",
        prevStep: "find_confirm_profile",
        condition: {
            partner: ["angi"],
        },
    },

    // ask for....
    /* {
        step: "facebook",
        prevStep: "auto_responders",
        disabled: true,
        // Can we get it from the GBP? - No actually
    },
    {
        // configure chat?
        prevStep: "business_info",
        step: "configure_chat",
        nextStep: "",
        condition: {
            intent: ["chat"],
        },
    },
    {
        // configure the booking link?
        step: "booking_link",
        condition: {
            intent: ["booking_link"],
        },
    },

    {
        // where do we send the leads?  Email or FSM
        step: "lead_destination",
        prevStep: "facebook",
        disabled: true,
    },
          Also
       - configure availability settings?
    */
    {
        step: "sign_up",
        nextStep: "create",
        prevStep: "find_confirm_profile",
    },
    {
        step: "create",
        nextStep: "checkout",
        prevStep: "lead_destination",
    },
    {
        step: "checkout",
        nextStep: "finalize",
    },
];

function determineNextStep(currentStep: Path, props: OnboardingProps, paths: Path[], organizationType?: string): string {
    console.log("determineNextStep", currentStep);
    console.log(props);
    const currentStepName = currentStep.step;
    const currentStepIndex = paths.findIndex((p) => p.step === currentStepName);
    const nextStepName = currentStep.nextStep;

    // find it by name, if defined
    let nextStep = paths.find((p) => p.step === nextStepName);
    // if we don't have the next step, just find the next one in the array, starting at the current, that is not disabled
    if (!nextStep) {
        nextStep = paths.find((p, index) => index > currentStepIndex && !p.disabled);
    }

    if (!nextStep) {
        console.error("determineNextStep: no next step found", currentStep);
        return currentStep.step;
    }

    if (nextStep.disabled) {
        // skip, go to the next that is not disabled
        console.log(`Skipping disabled step: ${nextStepName}`);
        nextStep = paths.find((p) => p.step === nextStep.nextStep);
    }

    // Skip checkout if organization type is PARTNER or PLATINUM
    if (nextStep.step === "checkout" && organizationType && (organizationType === "PARTNER" || organizationType === "PLATINUM")) {
        console.log(`Skipping checkout step for ${organizationType} organization`);
        return determineNextStep(nextStep, props, paths, organizationType);
    }

    // if we have a condition, check it
    if (nextStep?.condition) {
        console.log("determineNextStep condition", nextStep.condition);

        const needsWebsite = nextStep.condition.needsWebsite;

        // if the next step needs a website and we have one
        // get the next step
        if (needsWebsite && props.website) {
            console.log(`We have a website already, skipping ${nextStepName}`);
            const possibleNext = paths.find((p) => p.step === nextStep.step);
            return determineNextStep(possibleNext, props, paths, organizationType);
        }

        const canTrain = nextStep.condition.canTrain;
        // we need either a website or Google Business Profile
        if (canTrain && !props.website && !props.place?.placeId) {
            console.log(`${nextStepName} needs a website or GBP, skipping`);
            // we need to go back to the find_profile step
            const possibleNext = paths.find((p) => p.step === "find_profile");
            return determineNextStep(possibleNext, props, paths, organizationType);
        }

        const partner = props.beta?.onboarding?.partner;
        const availableToPartners = nextStep.condition.partner;
        // if the next step is only available for a specific partner
        if (existsAndNotEmpty(availableToPartners) && !availableToPartners.includes(partner)) {
            console.log(`${nextStepName} is not available for partner ${partner}, skipping`);
            // we need to go back to the find_profile step
            // const possibleNext = paths.find((p) => p.step === nextStep.nextStep);
            console.log("possible next?");
            return determineNextStep(nextStep, props, paths, organizationType);
        }
    }

    return nextStep.step;
}

interface OnboardingParams {
    placeId?: string;
    step?: string;
    appId?: string;
    organizationId?: string;
    executionId?: string;
    productId?: string;
    couponId?: string;
    partner?: string;

    [key: string]: string | undefined;
}

export interface OnboardingV3StepContainerProps {
    onboarding: OnboardingProps;
    onNext(props: OnboardingProps): void;
    onAlternate?(props: OnboardingProps): void;
}

//partner=&product=prod_QWRVWvZtQH6dBI&coupon=GrPPdkt1&freeTrial=false
export const OnboardingV3Container: FC = () => {
    const history = useHistory();

    const setQueryParams = useAddQueryParams();
    const queryParams = useQueryParams();

    const setPropsOnParams = useCallback(
        (step: string, props: OnboardingProps, additional: object = {}) => {
            const params: OnboardingParams = { step, ...additional };

            // We don't conditionally set them because we want to clear them out if they are not set
            params.placeId = props.place?.placeId;
            params.appId = props.appId;
            params.organizationId = props.organizationId;
            params.executionId = props.executionId;
            params.productId = props.productId;
            params.couponId = props.couponId;

            setQueryParams(params);
        },
        [setQueryParams]
    );

    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [answeredWarning, setAnsweredWarning] = useState<boolean>(false);

    // NEED REFACTOR
    // We are duplicating code here for getting the product & coupon and the special rules we have for Angi
    // Same code is in the CheckoutStepContainer
    const partner = queryParams.get("partner");
    const mode = queryParams.get("mode");
    let productId = queryParams.get("product") || queryParams.get("productId");

    console.log(`OnboardingV3Container initial values productId: ${productId}, partner: ${partner}, mode: ${mode}`);

    if (!productId) {
        productId = getDefaultProduct(partner);
    } else if (productId === "starter") {
        productId = getDefaultProduct(partner);
    }

    console.log("OnboardingV3Container productId:", productId);

    const product = getProduct(productId);
    const productName = partner?.toLowerCase() === "angi" ? product?.name?.replace("XAPP AI", "Angi") : product?.name;

    const couponId = queryParams.get("coupon") || queryParams.get("couponId");

    const coupon = getCoupon(couponId);

    console.log("OnboardingV3Container couponId:", couponId);
    console.log("OnboardingV3Container coupon:", coupon);

    // END NEED REFACTOR

    // Mode

    const ROUTE_B: boolean = true;

    let step: string = queryParams.get("step");
    if (!step) {
        console.log('no step, updating query params to "business_info"');
        console.log(productId);
        step = "business_info";
        // set query params - only include couponId if it's valid
        const params: { step: string; productId: string; partner?: string; couponId?: string } = {
            step,
            productId,
        };
        if (partner) {
            params.partner = partner;
        }
        if (couponId && couponId !== "null" && couponId !== "undefined") {
            params.couponId = couponId;
        }
        setQueryParams(params);
    }
    const appId = queryParams.get("appId");
    const organizationId = queryParams.get("organizationId");
    const debug = queryParams.get("debug");
    const executionId = queryParams.get("executionId");
    const cms = queryParams.get("cms");
    const placeId = queryParams.get("placeId");
    const useWebsite = queryParams.get("useWebsite") === "true";

    const route = ROUTE_B ? PATH_B : PATH_A;
    const currentStep = route.find((p) => p.step === step);
    // const prevStep = path.find((p) => p.step === currentStep?.prevStep);

    console.log("OnboardingV3Container step:", step);
    // console.log("OnboardingV3Container path:", currentStep);

    // HOOKS
    // TODO: MOVE THIS TO CONTAINER (OF ONBOARDING?)

    const [redirectUrl, setRedirectUrl] = useState<string>();

    const [analyzing, stopAnalyzing, startAnalyzing] = useBoolState(false);

    const [getProfile] = useGetUserProfileQuery({
        requestPolicy: "cache-and-network",
        variables: {},
    });

    const [getOrg] = useGetOrgQuery({
        pause: !organizationId,
        requestPolicy: "cache-and-network",
        variables: {
            orgId: organizationId,
        },
    });

    // If we land directly on checkout step and org is PARTNER/PLATINUM, skip to finalize
    const hasSkippedCheckout = useRef(false);
    const organizationType = getOrg.data?.org?.type;
    useEffect(() => {
        if (
            !hasSkippedCheckout.current &&
            step === "checkout" &&
            organizationType &&
            (organizationType === "PARTNER" || organizationType === "PLATINUM") &&
            !getOrg.fetching
        ) {
            console.log(`Skipping checkout step for ${organizationType} organization`);
            hasSkippedCheckout.current = true;
            setQueryParams({ step: "finalize" });
        }
    }, [step, organizationType, getOrg.fetching, setQueryParams]);

    const analyzeSite = wrapUrqlMutation(useAnalyzeWebsiteMutation(), {
        onSuccessMessage: false,
        onErrorMessage: true,
    });

    // These keep track of the props that will create the app as we move steps
    const [onboardingProps, setOnboardingProps] = useState<OnboardingProps>(() => {
        // in case the user refreshes, we store them (down below) on session storage
        // and try to retrieve them
        let storageProps: OnboardingProps;
        try {
            console.log('Onboarding, retrieving "onboarding-appProps" from session storage');
            storageProps = JSON.parse(sessionStorage.getItem("onboarding-appProps"));
        } catch (e) {
            // don't care about the error
            console.error(`Error parsing session storage onboarding-appProps: ${e}`);
        }
        return storageProps || {};
    });

    // do i update props here based on query params?
    onboardingProps.productId = productId;
    //onboardingProps.partnerId = partner;
    //onboardingProps.mode = mode;

    const [getAnalysis, executeGetAnalysis] = useGetWebsiteAnalysisQuery({
        pause: !onboardingProps.executionId,
        requestPolicy: "cache-and-network",
        variables: {
            executionId: onboardingProps.executionId,
        },
    });

    // This will monitor getAnalysis response and make a new request every 3 seconds until we get all the data
    useEffect(() => {
        if (
            onboardingProps.executionId &&
            !getAnalysis.fetching &&
            !getAnalysis.data?.onboarding?.webDetailsExecution?.completed
        ) {
            const timer = setInterval(() => {
                executeGetAnalysis({ requestPolicy: "network-only" });
            }, 3000);

            return () => {
                clearInterval(timer);
            };
        }
    }, [
        executeGetAnalysis,
        getAnalysis.data?.onboarding?.webDetailsExecution?.completed,
        getAnalysis.fetching,
        onboardingProps.executionId,
    ]);

    // This will look at the getAnalysis data and update the onboarding props with the new data
    useEffect(() => {
        // the onboardingProps.technologies will ONLY be populated once we get the data from the website analysis
        // it acts as a flag to know if we have all the data
        const alreadyUpdated = Array.isArray(onboardingProps?.technologies);

        if (getAnalysis?.data?.onboarding?.webDetailsExecution?.completed && !alreadyUpdated) {
            console.log("Onboarding! Analysis complete!!!!", getAnalysis.data.onboarding.webDetailsExecution.data);
            stopAnalyzing();
            // merge in a few details
            const copy = { ...onboardingProps };

            copy.technologies = getAnalysis.data.onboarding.webDetailsExecution.data.detectedTechnologies || [];

            const site = getAnalysis.data.onboarding.webDetailsExecution.data.site;

            const primaryColor = site?.primaryColor;
            if (!copy.primaryColor && primaryColor) {
                copy.primaryColor = primaryColor;
            }

            const secondaryColor = site?.secondaryColor;
            if (!copy.secondaryColor && secondaryColor) {
                copy.secondaryColor = secondaryColor;
            }

            const business = getAnalysis.data.onboarding.webDetailsExecution.data.business;
            const name = business?.name;
            if (!copy.name && name) {
                copy.name = name;
            }

            // check the template type
            const template = getAnalysis.data.onboarding.webDetailsExecution.data.business.category as TemplateId;
            // if we have one that is not the default (contact-capture) OR we don't have one on the props already, set it
            if (template && (!copy.template || copy.template?.id === "contact-capture")) {
                console.log("Setting template from analysis to:", template);
                copy.template = getTemplateFromId(template);
            }

            setOnboardingProps(copy);
        }
    }, [getAnalysis, onboardingProps, stopAnalyzing]);

    // This monitors the placeId query string and updates the onboarding props if it is different
    useEffect(() => {
        if (onboardingProps?.place?.placeId !== placeId) {
            setOnboardingProps((prev) => ({
                ...prev,
                placeId,
            }));
        }
    }, [onboardingProps?.place?.placeId, placeId]);

    // This monitors executionId and updates the onboarding props if it is different
    useEffect(() => {
        if (onboardingProps?.executionId !== executionId) {
            setOnboardingProps((prev) => ({
                ...prev,
                executionId,
            }));
        }
    }, [executionId, onboardingProps?.executionId]);

    // This monitors organizationId from query params and updates the onboarding props if it is different
    useEffect(() => {
        if (onboardingProps?.organizationId !== organizationId) {
            setOnboardingProps((prev) => ({
                ...prev,
                organizationId,
            }));
        }
    }, [organizationId, onboardingProps?.organizationId]);

    // This monitors partner, productId & couponId and updates the onboarding props if they are different
    useEffect(() => {
        if (onboardingProps?.beta?.onboarding?.partner !== partner) {
            setOnboardingProps((prev) => ({
                ...prev,
                beta: {
                    ...prev.beta,
                    onboarding: {
                        ...prev.beta?.onboarding,
                        partner,
                    },
                },
            }));
        }

        if (onboardingProps?.beta?.onboarding?.productId !== productId) {
            setOnboardingProps((prev) => ({
                ...prev,
                beta: {
                    ...prev.beta,
                    onboarding: {
                        ...prev.beta?.onboarding,
                        productId,
                    },
                },
            }));
        }

        if (onboardingProps?.beta?.onboarding?.couponId !== couponId) {
            setOnboardingProps((prev) => ({
                ...prev,
                beta: {
                    ...prev.beta,
                    onboarding: {
                        ...prev.beta?.onboarding,
                        couponId,
                    },
                },
            }));
        }
    }, [
        couponId,
        onboardingProps?.beta?.onboarding?.couponId,
        onboardingProps?.beta?.onboarding?.partner,
        onboardingProps?.beta?.onboarding?.productId,
        partner,
        productId,
    ]);

    // MASSIVE!
    // This will take over all new handleNext methods
    const handleNext = useCallback(
        (props: OnboardingProps) => {
            console.log("handleNext", props);
            let updatedProps = props;

            // if we have a website and no executionId, start website analysis to run in the background
            if (props.website && !onboardingProps.executionId && !analyzing) {
                console.log("OnboardingV3Container handleNext: starting website analysis");
                const url = new URL(props.website || "");
                // remove query params
                url.search = "";
                const cleanWebsite = url.toString();
                startAnalyzing();
                analyzeSite({ url: cleanWebsite }).then((result) => {
                    console.log("Received executionId");
                    const executionId = result.data?.onboarding?.startRetrieveWebsiteDetails?.executionId;
                    setOnboardingProps((prev) => ({ ...prev, executionId }));
                    setQueryParams({
                        executionId,
                    });
                });
            }

            sendOnboardingEvent(currentStep?.step, updatedProps);

            setOnboardingProps((prev) => {
                const newProps: OnboardingProps = {
                    ...prev,
                    ...props,
                };
                updatedProps = newProps;

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newProps));
                return newProps;
            });

            const organizationType = getOrg.data?.org?.type;
            const nextStep = determineNextStep(currentStep, props, route, organizationType);
            console.log("OnboardingV3Container.handleNext nextStep:", nextStep);
            setPropsOnParams(nextStep, updatedProps);
        },
        [
            analyzeSite,
            analyzing,
            currentStep,
            getOrg.data?.org?.type,
            onboardingProps.executionId,
            route,
            setPropsOnParams,
            setQueryParams,
            startAnalyzing,
        ]
    );

    // DEPRECATED
    // They have selected a placeId and need to move on to confirm
    const handlePlaceId = useCallback(
        (place: Place) => {
            setQueryParams({
                step: "confirm_profile",
                placeId: place.placeId,
            });

            sendOnboardingEvent("find_place", onboardingProps);
        },
        [setQueryParams, onboardingProps]
    );

    // DEPRECATED
    const handleUseAdvancedSearch = useCallback(
        (search?: string) => {
            setOnboardingProps((prev) => {
                const newProps: OnboardingProps = {
                    ...prev,
                    name: search,
                };

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newProps));
                return newProps;
            });

            setQueryParams({
                step: "advanced_search",
                search,
            });
        },
        [setQueryParams]
    );

    /**
     * We found a profile or enough information to move on
     *
     * DEPRECATED
     */
    const handleAdvancedSearchResult = useCallback(
        (props: OnboardingProps) => {
            setOnboardingProps((prev) => {
                const newProps: OnboardingProps = {
                    ...prev,
                    ...props,
                };

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newProps));

                return newProps;
            });

            setQueryParams({
                step: "confirm_profile",
                placeId: props.place?.placeId,
            });
        },
        [setQueryParams]
    );

    // DEPRECATED
    const handleNoPlaceSelected = useCallback(
        (props: OnboardingProps) => {
            setOnboardingProps((prev) => {
                const newProps: OnboardingProps = {
                    ...prev,
                    ...props,
                };

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newProps));

                return newProps;
            });

            setQueryParams({
                step: "claim_agent",
            });
        },
        [setQueryParams]
    );

    // DEPRECATED
    // They have confirmed the profile and need to move on to the next step
    const handleConfirmProfile = useCallback(
        (props: OnboardingProps) => {
            setOnboardingProps((prev) => {
                const newProps: OnboardingProps = {
                    ...prev,
                    ...props,
                };

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newProps));

                return newProps;
            });
            setQueryParams({
                step: "training",
            });
        },
        [setQueryParams]
    );

    // DEPRECATED
    // They selected they want to use the website instead of google business profile
    const handleUseWebsite = useCallback(() => {
        setQueryParams({
            useWebsite: "true",
            step: "use_website",
        });
    }, [setQueryParams]);

    // DEPRECATED
    const handleWebsite = useCallback(
        (url: string) => {
            setOnboardingProps((prev) => {
                const logo = getLogoFromWebsite(url);

                const newProps: OnboardingProps = {
                    ...prev,
                    website: url,
                    logo,
                };

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newProps));

                return newProps;
            });
            setQueryParams({
                step: "training",
            });
        },
        [setQueryParams]
    );

    // DEPRECATED
    // Training complete, move them to claim agent
    const handleTrainingComplete = useCallback(
        (newProps: OnboardingProps) => {
            setOnboardingProps((prev) => {
                const props: OnboardingProps = {
                    ...prev,
                    ...newProps,
                };

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(props));

                return props;
            });
            setQueryParams({
                executionId: newProps.executionId,
                step: "claim_agent",
            });
        },
        [setQueryParams]
    );

    // DEPRECATED
    const handleClaimAgent = useCallback(() => {
        // take them to login
        setQueryParams({
            step: "sign_up",
        });
    }, [setQueryParams]);

    // DEPRECATED
    const handleSignUpSuccess = useCallback(
        (props: OnboardingProps) => {
            setOnboardingProps((prev) => {
                const newProps: OnboardingProps = {
                    ...prev,
                    ...props,
                };

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newProps));

                return newProps;
            });

            setQueryParams({
                step: "create",
            });
        },
        [setQueryParams]
    );

    // DEPRECATED
    const handleCreateApp = useCallback(
        (props: OnboardingProps) => {
            setOnboardingProps((prev) => {
                const newProps: OnboardingProps = {
                    ...prev,
                    ...props,
                };

                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newProps));

                return newProps;
            });

            setQueryParams({
                appId: props.appId,
                organizationId: props.organizationId,
                step: "checkout",
            });
        },
        [setQueryParams]
    );

    // Move them to finalize
    // const handleFinalize = useCallback(() => {}, []);

    const handleRedirect = useCallback(() => {
        // Clear out the onboarding props from session storage?
        sessionStorage.removeItem("onboarding-appProps");

        // Redirect them to app home
        let successPath = `/setup/${appId}?checkout=success`;
        if (cms) {
            successPath += `&cms=${cms}`;
        }

        if (partner) {
            successPath += `&partner=${partner}`;
        }

        // go to the successPath
        setRedirectUrl(successPath);
    }, [appId, cms, partner]);

    // BACK BUTTON
    const handleBack = useCallback(() => {
        const firstStep = route[0].step;

        // get current step path
        const currentStep = route.find((p) => p.step === step);

        const previousStep = currentStep?.prevStep;

        // if we are going to the first step, clear everything
        const index = route.findIndex((p) => p.step === previousStep);
        const prevStep = route[index];

        console.log("OnboardingV3Container handleBack step:", step);
        console.log("OnboardingV3Container handleBack path:", currentStep);
        console.log("OnboardingV3Container handleBack index:", index);
        console.log("OnboardingV3Container handleBack firstStep:", firstStep);

        if (prevStep?.cleanProps) {
            console.log("clearing onboarding props");
            sessionStorage.setItem("onboarding-appProps", JSON.stringify({}));
            setOnboardingProps({});
            // clear out the query params
        }

        setQueryParams({
            step: currentStep?.prevStep || firstStep,
        });
    }, [route, setQueryParams, step]);

    // DEBUG USE ONLY
    const clearAppProps = useCallback(() => {
        setPropsOnParams("business_info", {});
        sessionStorage.removeItem("onboarding-appProps");
    }, [setPropsOnParams]);

    if (redirectUrl) {
        return <Redirect to={redirectUrl} />;
    }

    // Data
    const { data } = getProfile;

    const profile = data?.profile;

    const roles = profile?.roles || [];

    if (roles.length === 1) {
        // TODO: get the app on the org
    }

    if (roles.length >= 1 && debug?.toLowerCase() !== "true" && !answeredWarning && !appId && !organizationId) {
        // let them know they have multiple
        // Skip warning if organizationId is provided - they're intentionally creating a new app for existing org
        if (!showWarning) {
            setShowWarning(true);
        }
    }

    const warningActions: ButtonProps[] = [
        {
            label: "Continue",
            variation: "secondary",
            onClick: () => {
                setAnsweredWarning(true);
                setShowWarning(false);
            },
        },
        {
            label: "Show me Existing",
            onClick: () => {
                setRedirectUrl(`/list`);
            },
        },
    ];

    // debug mode if we are on localhost
    const DEBUG = window.location.hostname === "localhost";

    // success path after checkout
    // copy the params
    const successPathParams = new URLSearchParams(history.location.search);
    successPathParams.set("step", "finalize");
    const successPath = `/onboarding?${successPathParams.toString()}`;

    const isAdmin = !!profile?.isAdmin;
    // Only allow skipping checkout if we have website analysis data
    const hasWebsiteData = Array.isArray(onboardingProps?.technologies);
    const handleSkipCheckout = useCallback(() => {
        setQueryParams({ step: "finalize" });
    }, [setQueryParams]);

    return (
        <>
            <div className={style.headerImageWrapper}>
                {/* CHANGE THIS FOR ANGI */}
                {partner === "angi" ? (
                    <img src={"/img/partner/xapp-angi.png"} alt="logo" className={style.headerImage} />
                ) : (
                    <img src={"/img/xapp-ai-horizontal-2color-rgb.png"} alt="logo" className={style.headerImage} />
                )}
            </div>
            <Onboarding
                steps={{
                    bottom: (
                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <Pricing
                                name={productName}
                                price={product.price}
                                priceInCents={product.priceInCents}
                                trialDays={undefined}
                                coupon={coupon}
                                config={{
                                    compact: true,
                                    fontSize: "0.8rem",
                                }}
                                clarificationText="No additional cost per lead"
                            />
                            {profile && profile.email && (
                                <div style={{ width: "100%", textAlign: "center" }}>
                                    <small>👋Hello {profile.email}! </small>
                                </div>
                            )}
                        </div>
                    ),
                }}
                progressBar={{
                    type: step === "intent" ? "none" : "loading-bar", // Hide the progress bar on the intent step
                    rightContent: null,
                    disableBack: currentStep?.prevStep === undefined,
                    disableNumericalProgress: true,
                    onBack: step !== "find_profile" ? handleBack : undefined,
                }}
            >
                <OnboardingStep
                    active={step === "intent"}
                    done={step !== "intent"}
                    hide={true}
                    header={getStepHeader("intent")}
                    title="Goal"
                >
                    <IntentQuestionStep
                        className={style.onboardingContent}
                        aside={getStepAside("intent", onboardingProps?.place, queryParams)}
                        initialProps={onboardingProps}
                        onNext={handleNext}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "business_info"}
                    done={step !== "business_info"}
                    hide={!route.some((p) => p.step === "business_info")}
                    header={getStepHeader("business_info", onboardingProps?.place, queryParams)}
                    title="Business Info"
                >
                    <BusinessInfoStepContainer
                        className={style.onboardingContent}
                        initialProps={onboardingProps}
                        aside={getStepAside("business_info", onboardingProps?.place, queryParams)}
                        onBack={handleBack}
                        onNext={handleNext}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "find_confirm_profile"}
                    done={step !== "find_confirm_profile"}
                    hide={true}
                    header={getStepHeader("find_confirm_profile")}
                    title="Confirm"
                >
                    <FindAndConfirmProfileStepContainer
                        requirePlace={true}
                        initialProps={onboardingProps}
                        aside={getStepAside("find_confirm_profile")}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep>

                {/* Keeping this if they have a profile but now website, we still want it */}
                <OnboardingStep
                    active={step === "website"}
                    done={step !== "website"}
                    hide={true}
                    header={getStepHeader("website")}
                    title="Website"
                >
                    <WebsiteQuestionStepContainer
                        initialProps={onboardingProps}
                        aside={getStepAside("website")}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "auto_responders"}
                    done={step !== "auto_responders"}
                    hide={true}
                    header={getStepHeader("auto_responders")}
                    title="Auto Responders"
                >
                    <AngiAutoResponderQuestionStepContainer
                        initialProps={onboardingProps}
                        aside={getStepAside("auto_responders")}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "facebook"}
                    done={step !== "facebook"}
                    hide={true}
                    header={getStepHeader("facebook")}
                    title="Facebook"
                >
                    <FacebookPageQuestionStepContainer
                        initialProps={onboardingProps}
                        aside={getStepAside("facebook")}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "lead_destination"}
                    done={step !== "lead_destination"}
                    hide={true}
                    header={getStepHeader("lead_destination")}
                    title="Lead Destination"
                >
                    <LeadDestinationStep
                        aside={getStepAside("lead_destination")}
                        initialProps={onboardingProps}
                        fsmListStyle="dropdown"
                        onBack={handleBack}
                        onNext={handleNext}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "find_profile"}
                    done={step !== "find_profile"}
                    hide={false}
                    header={getStepHeader("find_profile")}
                    title="Find"
                >
                    <AddressStep
                        aside={getStepAside("find_profile")}
                        onPlaceSelected={handlePlaceId}
                        onUseWebsite={handleUseWebsite}
                        onUseAdvancedSearch={handleUseAdvancedSearch}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "advanced_search"}
                    done={step === "find_profile"}
                    hide={true}
                    header={getStepHeader("advanced_search")}
                    title="Advanced Search"
                >
                    <AdvancedSearchStepContainer
                        aside={getStepAside("advanced_search")}
                        onboarding={onboardingProps}
                        onNext={handleAdvancedSearchResult}
                        onNoPlaceSelected={handleNoPlaceSelected}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "confirm_profile"}
                    done={step === "claim_agent"}
                    hide={useWebsite}
                    header={getStepHeader("confirm_profile")}
                    title={"Confirm"}
                >
                    <ConfirmProfileContainer
                        aside={getStepAside("confirm_profile", onboardingProps?.place, queryParams)}
                        placeId={placeId}
                        onboarding={onboardingProps}
                        onConfirm={handleConfirmProfile}
                        onSearchAgain={handleBack.bind(null, "confirm_profile")}
                    />
                </OnboardingStep>

                {/* Optional Route */}
                <OnboardingStep
                    active={step === "use_website"}
                    done={step === "claim_agent"}
                    hide={!useWebsite}
                    header={getStepHeader("use_website", onboardingProps?.place, queryParams)}
                    title={"Website"}
                >
                    <WebsiteStep
                        aside={getStepAside("use_website", onboardingProps?.place, queryParams)}
                        onWebsite={handleWebsite}
                        onSearchAgain={handleBack.bind(null, "use_website")}
                    />
                </OnboardingStep>

                {/* Back to original route */}
                <OnboardingStep
                    active={step === "training"}
                    done={false}
                    hide={true}
                    title={"Training"}
                    header={getStepHeader("training", onboardingProps?.place, queryParams)}
                >
                    <TrainingStepContainer
                        onInitialData={handleTrainingComplete}
                        onTimeout={handleTrainingComplete}
                        timeout={6000}
                        aside={getStepAside("training")}
                        onboarding={onboardingProps}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "claim_agent"}
                    done={step === "sign_up"}
                    header={getStepHeader("claim_agent", onboardingProps?.place, queryParams)}
                    title={"Claim"}
                >
                    <ClaimAgentStep
                        aside={getStepAside("claim_agent", onboardingProps?.place, queryParams)}
                        onboarding={onboardingProps}
                        onClaim={handleClaimAgent}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "customize"}
                    done={step === "sign_up"}
                    title={"Congratulations"}
                    hide={false}
                    header={getStepHeader("customize", onboardingProps?.place, queryParams)}
                >
                    <CustomizeStepContainer
                        aside={getStepAside("customize", onboardingProps?.place, queryParams)}
                        initialProps={onboardingProps}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "sign_up"}
                    done={false}
                    title={"Sign Up"}
                    hide={false}
                    header={getStepHeader("sign_up", onboardingProps?.place, queryParams)}
                >
                    <SignUpStepContainer
                        aside={getStepAside("sign_up", onboardingProps?.place, queryParams)}
                        onboarding={onboardingProps}
                        onSignUpSuccess={handleSignUpSuccess}
                    />
                </OnboardingStep>

                {/* We then get redirected to creating the app on this step */}
                <OnboardingStep
                    active={step === "create"}
                    done={false}
                    hide={true}
                    title={"Create"}
                    header={getStepHeader("create", onboardingProps?.place, queryParams)}
                >
                    <ConfigureAndCreateStepContainer
                        debug={DEBUG}
                        product={productId}
                        onboarding={onboardingProps}
                        profile={profile}
                        onNextStep={handleCreateApp}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "checkout"}
                    done={false}
                    title={"checkout"}
                    header={getStepHeader("checkout", onboardingProps?.place, queryParams)}
                >
                    <CheckoutStepContainer
                        onboarding={onboardingProps}
                        productId={productId}
                        couponId={couponId}
                        partner={partner}
                        noTrial={true}
                        successPath={successPath}
                        integrationsSupported={supportsIntegrations(productId)}
                        isAdmin={isAdmin}
                        onSkip={isAdmin && hasWebsiteData ? handleSkipCheckout : undefined}
                    />
                </OnboardingStep>

                <OnboardingStep
                    active={step === "finalize"}
                    done={false}
                    hide={true}
                    title={"complete"}
                    header={getStepHeader("finalize", onboardingProps?.place, queryParams)}
                >
                    <FinalizeContainer
                        onboardingProps={onboardingProps}
                        profile={profile}
                        onNextStep={handleRedirect}
                    />
                </OnboardingStep>
            </Onboarding>

            <Dialog active={showWarning} title={"Are you sure you want to start over?"} actions={warningActions}>
                <p>You have already setup at least one business.</p>
            </Dialog>

            {debug === "true" && (
                <div
                    style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignContent: "center",
                        alignItems: "flex-start",
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flexWrap: "wrap" }}
                    >
                        <b>Debug</b>
                        <p>Business: {onboardingProps?.name}</p>
                        <p>Search Address: {onboardingProps?.searchAddress}</p>
                        <p>Organization ID: {onboardingProps.organizationId}</p>
                        <p>App: {onboardingProps?.appId}</p>
                        <p>
                            Template: {onboardingProps?.template?.name} ({onboardingProps?.template?.id})
                        </p>
                        <p>Website: {onboardingProps?.website}</p>
                        <p>Execution Id: {onboardingProps?.executionId}</p>
                        <p>Web Colors: {onboardingProps?.primaryColor}</p>
                        <p>
                            Place: {onboardingProps?.place?.formattedAddress} ({onboardingProps?.place?.placeId})
                        </p>
                        <p>
                            Integration:{" "}
                            {existsAndNotEmpty(onboardingProps?.beta?.onboarding?.leadsIntegration)
                                ? onboardingProps?.beta?.onboarding?.leadsIntegration[0]
                                : ""}
                        </p>
                        <p>Leads Email: {onboardingProps?.leadsContact}</p>
                        <p>
                            Channels: {onboardingProps.lexChannelId} {onboardingProps.chatWidgetId}-
                            {onboardingProps.formWidgetId}-{onboardingProps.searchWidgetId}
                        </p>
                        <p>
                            Product: {productName} @ {product.price}
                        </p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", margin: "20px", padding: "10px" }}>
                        <Button variation="secondary" label="Clear Props" onClick={clearAppProps} />
                        <Button label="Print Props" onClick={() => console.log(onboardingProps)} />
                    </div>
                </div>
            )}
        </>
    );
};
